"use client";

import HeroSection from "@/components/page-section/HeroSection/HeroSection";
import type { PageContent } from "@/services/api/page-contents";
import {
  overlayDefaultsFromCmsFields,
  slideItemsFromCmsRows,
  type CmsHeroSlideRow,
} from "@/utils/cms-hero-slides";
import { normalizeCmsAssetUrl } from "@/utils/media-url";

interface ThacSiHeroProps {
  content?: PageContent | null;
}

type HeroData = {
  type?: string;
  imageUrl?: string;
  mobileImageUrl?: string;
  alt?: string;
  slides?: CmsHeroSlideRow[];
  brand?: string;
  title?: string;
  button?: {
    label?: string;
    href?: string;
    variant?: string;
    openInNewTab?: boolean;
    target?: string;
  };
};

const isHeroContent = (v: unknown): v is HeroData =>
  !!v && typeof v === "object" && (v as HeroData).type === "thac-si-hero-section";

const FALLBACK = {
  brand: "HUB",
  title: "THẠC SĨ",
  label: "Xem chi tiết",
  href: "#about",
};

export default function ThacSiHero({ content }: ThacSiHeroProps) {
  const resolved = (() => {
    const raw = content?.content;
    if (!raw) return null;
    if (typeof raw === "string") {
      try {
        const p = JSON.parse(raw);
        return isHeroContent(p) ? p : null;
      } catch {
        return null;
      }
    }
    return isHeroContent(raw) ? raw : null;
  })();

  const heroSlides = (() => {
    const fromSlides = slideItemsFromCmsRows(resolved?.slides ?? null);
    if (fromSlides) {
      return fromSlides.map((s, i) =>
        i === 0 && resolved?.mobileImageUrl
          ? { ...s, mobileImage: resolved.mobileImageUrl }
          : s,
      );
    }
    if (resolved?.imageUrl) {
      const img = normalizeCmsAssetUrl(resolved.imageUrl) || resolved.imageUrl;
      return [
        {
          image: img,
          mobileImage: resolved.mobileImageUrl ?? null,
          alt: resolved.alt ?? "Banner Thạc sĩ",
        },
      ];
    }
    return null;
  })();

  const hasNewCmsShape = !!resolved?.slides?.length;

  const overlayDefaults = hasNewCmsShape
    ? overlayDefaultsFromCmsFields(
        resolved?.brand,
        resolved?.title,
        resolved?.button,
        FALLBACK,
      )
    : null;

  return (
    <HeroSection
      pageKey="thac-si"
      variant="carousel"
      sectionModifier="hero-section--banner hero-section--thacsi"
      ariaLabel="Banner Thạc sĩ"
      overrideSlides={heroSlides}
      overlayDefaults={overlayDefaults}
      breadcrumb={[
        { label: "Trang chủ", href: "/" },
        { label: "Sau đại học", href: "/sau-dai-hoc" },
        { label: "Thạc sĩ" },
      ]}
    />
  );
}
